import React, {useEffect, useRef, useState} from "react";
import {CheckIcon, NoSymbolIcon} from "@heroicons/react/24/solid";
import {useDispatch, useSelector} from "react-redux";
import {selectProgressStatus} from "../../api/progressStatus/progressStatusService";
import {getContentStatus} from "../../api/contentStatus/contentStatusService";
import {changeProgressStatusValue, changeStatusNumericValue} from "../../redux/slice/contentSlice";
import {ContentStatus} from "../../constant/CodeSpecNum";
import {isMobile} from "../commonmethod/CommonMethod";
import {getStepToneStyle} from "./stepperPalette";
import "./stepper.css";

const ACTIVE_MIN = ContentStatus.APPROVE.statusNumericValue;

function resolveStepState(step, currentStepOrder, progress) {
    const isRejected = progress.rejected === ContentStatus.REJECT.statusValue;
    const isComplete = progress.statusNumericValue === ContentStatus.COMPLETE.statusNumericValue;

    if (isComplete) {
        return "done";
    }
    if (isRejected && currentStepOrder === step.order) {
        return "rejected";
    }
    if (currentStepOrder > step.order) {
        return "done";
    }
    if (currentStepOrder === step.order) {
        return Number(progress.statusNumericValue) < ACTIVE_MIN ? "early" : "active";
    }
    return "pending";
}

function resolveStepLabel(step, state) {
    if (state === "done") {
        return step.completeStageKorValue;
    }
    if (state === "rejected") {
        return step.rejectStageKorValue;
    }
    return step.korValue;
}

function StepIcon({state, order}) {
    if (state === "done") {
        return <CheckIcon className="h-3.5 w-3.5" aria-hidden />;
    }
    if (state === "rejected") {
        return <NoSymbolIcon className="h-3.5 w-3.5" aria-hidden />;
    }
    return <span>{order}</span>;
}

function StepperTrack({steps, currentStepOrder, currentProgressStatus, mobile}) {
    const isRejected = currentProgressStatus.rejected === ContentStatus.REJECT.statusValue;

    return (
        <div className={`simms-stepper ${mobile ? "is-mobile" : ""}`}>
            <div className={`simms-stepper-track ${mobile ? "is-mobile" : ""}`} role="list" aria-label="진행단계">
                {steps.map((step, index) => {
                    const state = resolveStepState(step, currentStepOrder, currentProgressStatus);
                    const label = resolveStepLabel(step, state);
                    const stateClass = state === "early" ? "is-early is-active" : `is-${state}`;

                    return (
                        <div
                            key={step.numericValue ?? step.order ?? index}
                            role="listitem"
                            className={`simms-stepper-step ${stateClass} ${
                                index === 0 ? "is-first" : ""
                            } ${index === steps.length - 1 ? "is-last" : ""}`}
                            style={getStepToneStyle(index)}
                            title={label}
                            aria-current={state === "active" || state === "early" || state === "rejected" ? "step" : undefined}
                        >
                            <span className="simms-stepper-step__order">
                                <StepIcon state={state} order={step.order} />
                            </span>
                            <span className="simms-stepper-step__label">{label}</span>
                        </div>
                    );
                })}
            </div>

            {isRejected && currentProgressStatus.rejectUser && (
                <p className="simms-stepper-reject-meta">
                    {`반려자 : ${
                        currentProgressStatus.rejectUser?.department
                            ? `[${currentProgressStatus.rejectUser.department.name}] `
                            : ""
                    }${currentProgressStatus.rejectUser?.name ?? ""}`}
                </p>
            )}
        </div>
    );
}

/**
 * steps 정의
 *   contentId: 컨텐츠의 키값
 *   contentCategory: 백엔드 컨텐츠 카테고리 (ContentCategory)
 */
const Stepper = ({contentId, contentCategory}) => {
    const [currentStepOrder, setCurrentStepOrder] = useState(0);
    const [steps, setSteps] = useState([]);
    const [currentProgressStatus, setCurrentProgressStatus] = useState({});
    const [ready, setReady] = useState(false);

    const dispatch = useDispatch();
    const refreshContent = useSelector((state) => state.status.refreshContent);
    const requestSeq = useRef(0);
    const mobile = isMobile;

    useEffect(() => {
        if (contentId == null || !contentCategory) {
            setSteps([]);
            setCurrentStepOrder(0);
            setCurrentProgressStatus({});
            setReady(false);
            return;
        }

        const seq = ++requestSeq.current;
        setSteps([]);
        setCurrentStepOrder(0);
        setCurrentProgressStatus({});
        setReady(false);

        const load = async () => {
            const [progressRes, statusRes] = await Promise.all([
                selectProgressStatus(contentCategory),
                getContentStatus({contentId, contentCategory}),
            ]);

            if (seq !== requestSeq.current) {
                return;
            }

            if (progressRes?.status === 200) {
                const progress = progressRes.data.map((item, index) => ({...item, order: index + 1}));
                const numericValue = statusRes?.status === 200
                    ? statusRes.data.statusNumericValue
                    : 0;
                const step = progress.find((s) => s.numericValue === numericValue) || {order: 1};

                setSteps(progress);
                setCurrentStepOrder(step.order);
            }

            if (statusRes?.status === 200) {
                setCurrentProgressStatus(statusRes.data);
                dispatch(changeStatusNumericValue(statusRes.data.statusNumericValue));
                dispatch(changeProgressStatusValue(statusRes.data));
            }

            setReady(true);
        };

        load();
    }, [contentId, contentCategory, refreshContent, dispatch]);

    if (!ready || steps.length === 0) {
        return null;
    }

    return (
        <StepperTrack
            steps={steps}
            currentStepOrder={currentStepOrder}
            currentProgressStatus={currentProgressStatus}
            mobile={mobile}
        />
    );
};

export default Stepper;
